<template>
  <b-modal
    :id="modalId"
    :title="$t('Import from Excel')"
    size="lg"
    centered
    no-close-on-backdrop
    @hidden="resetModal"
  >
    <div v-if="!validationResult">
      <!-- Upload section -->
      <div class="mb-2">
        <h5>{{ $t('Step 1: Upload Excel file') }}</h5>
        <p class="text-muted">
          {{ $t('Upload an Excel file with RAT template v1.1 structure') }}
        </p>

        <div
          class="dropzone"
          :class="{ 'drag-over': isDragging }"
          @drop.prevent="handleDrop"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            style="display: none"
            @change="handleFileSelect"
          >

          <div v-if="!selectedFile" class="text-center py-3">
            <feather-icon icon="UploadCloudIcon" size="48" class="text-primary mb-2" />
            <p class="mb-1">
              {{ $t('Drag and drop Excel file here') }}
            </p>
            <p class="text-muted mb-2">{{ $t('or') }}</p>
            <b-button
              variant="outline-primary"
              @click="$refs.fileInput.click()"
            >
              {{ $t('Select File') }}
            </b-button>
          </div>

          <div v-else class="selected-file">
            <feather-icon icon="FileTextIcon" size="24" class="text-success" />
            <span class="ml-1">{{ selectedFile.name }}</span>
            <b-button
              variant="flat-danger"
              size="sm"
              class="ml-auto"
              @click="removeFile"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </div>

        <div class="mt-2">
          <a
            href="#"
            class="text-primary"
            @click.prevent="downloadTemplate"
          >
            <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
            {{ $t('Download import template') }}
          </a>
        </div>
      </div>

      <!-- Validate button -->
      <div class="mt-3">
        <b-button
          variant="primary"
          :disabled="!selectedFile || isProcessing"
          @click="validateFile"
        >
          <b-spinner v-if="isProcessing" small class="mr-50" />
          <feather-icon v-else icon="CheckCircleIcon" class="mr-50" />
          {{ $t('Validate before importing') }}
        </b-button>
      </div>
    </div>

    <!-- Validation results -->
    <div v-else>
      <h5 class="mb-2">{{ $t('Validation Results') }}</h5>

      <!-- Summary cards -->
      <b-row class="mb-2">
        <b-col md="4">
          <b-card class="text-center">
            <h3 class="mb-0">{{ validationResult.totalRows }}</h3>
            <p class="text-muted mb-0">{{ $t('Total rows') }}</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center border-success">
            <h3 class="mb-0 text-success">{{ validationResult.validRows }}</h3>
            <p class="text-muted mb-0">{{ $t('Valid rows') }}</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center border-danger">
            <h3 class="mb-0 text-danger">{{ validationResult.invalidRows }}</h3>
            <p class="text-muted mb-0">{{ $t('Rows with errors') }}</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Errors list -->
      <div v-if="validationResult.errors.length > 0" class="mb-2">
        <h6 class="text-danger">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-50" />
          {{ $t('Errors') }} ({{ validationResult.errors.length }})
        </h6>
        <b-card class="border-danger">
          <ul class="mb-0 pl-3">
            <li
              v-for="(error, index) in validationResult.errors.slice(0, 10)"
              :key="index"
              class="text-danger small"
            >
              {{ error }}
            </li>
          </ul>
          <p v-if="validationResult.errors.length > 10" class="text-muted small mb-0 mt-1">
            {{ $t('... and {count} more errors', { count: validationResult.errors.length - 10 }) }}
          </p>
        </b-card>
      </div>

      <!-- Warnings list -->
      <div v-if="validationResult.warnings.length > 0" class="mb-2">
        <h6 class="text-warning">
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50" />
          {{ $t('Warnings') }} ({{ validationResult.warnings.length }})
        </h6>
        <b-card class="border-warning">
          <ul class="mb-0 pl-3">
            <li
              v-for="(warning, index) in validationResult.warnings.slice(0, 10)"
              :key="index"
              class="text-warning small"
            >
              {{ warning }}
            </li>
          </ul>
          <p v-if="validationResult.warnings.length > 10" class="text-muted small mb-0 mt-1">
            {{ $t('... and {count} more warnings', { count: validationResult.warnings.length - 10 }) }}
          </p>
        </b-card>
      </div>

      <!-- Action buttons -->
      <div class="d-flex justify-content-between mt-3">
        <b-button
          variant="outline-secondary"
          @click="resetValidation"
        >
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          {{ $t('Upload another file') }}
        </b-button>

        <b-button
          variant="success"
          :disabled="validationResult.validRows === 0 || isProcessing"
          @click="proceedImport"
        >
          <b-spinner v-if="isProcessing" small class="mr-50" />
          <feather-icon v-else icon="UploadIcon" class="mr-50" />
          {{ $t('Import {count} treatments', { count: validationResult.validRows }) }}
        </b-button>
      </div>
    </div>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        @click="$bvModal.hide(modalId)"
      >
        {{ $t('message.cancel') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ref } from '@vue/composition-api'
import { BModal, BButton, BCard, BRow, BCol, BSpinner } from 'bootstrap-vue'
import useImportRAT from './useImportRAT'
import useExportRAT from './useExportRAT'
import useNotifications from '@/composables/useNotifications'

export default {
  components: {
    BModal,
    BButton,
    BCard,
    BRow,
    BCol,
    BSpinner
  },
  props: {
    modalId: {
      type: String,
      default: 'import-rat-modal'
    }
  },
  setup(props, { emit, root }) {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { exportToExcel } = useExportRAT()
    const {
      isProcessing,
      validationResult,
      importResult,
      validateFile: validateFileComposable,
      importTreatments
    } = useImportRAT()

    const selectedFile = ref(null)
    const fileInput = ref(null)
    const isDragging = ref(false)

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        selectedFile.value = file
      }
    }

    const handleDrop = (event) => {
      isDragging.value = false
      const file = event.dataTransfer.files[0]
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        selectedFile.value = file
      } else {
        showErrorMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)')
      }
    }

    const removeFile = () => {
      selectedFile.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const validateFile = async () => {
      try {
        await validateFileComposable(selectedFile.value)
      } catch (error) {
        console.error('Error validating file:', error)
        showErrorMessage(error.message || 'Error al validar el archivo')
      }
    }

    const proceedImport = async () => {
      try {
        const result = await importTreatments()

        if (result.success) {
          showSuccessMessage(
            `${result.imported} tratamientos importados correctamente` +
            (result.failed > 0 ? `. ${result.failed} filas con error omitidas.` : '')
          )

          emit('import-success', result)

          // Close modal after successful import
          root.$bvModal.hide(props.modalId)

          resetModal()
        }
      } catch (error) {
        console.error('Error importing treatments:', error)
        showErrorMessage(error.message || 'Error al importar tratamientos')
      }
    }

    const downloadTemplate = () => {
      // Export an empty template for user to fill (only RAT sheet, no Instructivo)
      exportToExcel([], true)
    }

    const resetValidation = () => {
      validationResult.value = null
    }

    const resetModal = () => {
      selectedFile.value = null
      validationResult.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    return {
      selectedFile,
      fileInput,
      isDragging,
      isProcessing,
      validationResult,
      handleFileSelect,
      handleDrop,
      removeFile,
      validateFile,
      proceedImport,
      downloadTemplate,
      resetValidation,
      resetModal
    }
  }
}
</script>

<style lang="scss" scoped>
.dropzone {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem;
  transition: all 0.3s;

  &.drag-over {
    border-color: #7367f0;
    background-color: #f8f8f8;
  }
}

.selected-file {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #f8f8f8;
  border-radius: 4px;
}
</style>
